// src/pages/admin/DataResetPage.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  resetAppData,
  previewReset,
  canResetData,
  ResetProgress,
  ResetResult,
} from '../../utils/dataReset';
import {
  AlertTriangle,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Shield,
  Database,
  Users,
  Loader2,
  Link2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { collection, getDocs, doc, updateDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';

interface PreviewData {
  collections: { name: string; count: number }[];
  users: number;
  total: number;
}

const DataResetPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  const [hasPermission, setHasPermission] = useState(false);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [progress, setProgress] = useState<ResetProgress[]>([]);
  const [progressMessages, setProgressMessages] = useState<string[]>([]);
  const [result, setResult] = useState<ResetResult | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleteUsers, setDeleteUsers] = useState(true);

  const CONFIRM_PHRASE = 'DELETE ALL DATA';

  // Sync Project Users state
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ usersAddedToProjects: number; projectsAddedToUsers: number } | null>(null);

  const handleSyncProjectUsers = async () => {
    setIsSyncing(true);
    setSyncResult(null);
    let usersAddedToProjects = 0;
    let projectsAddedToUsers = 0;

    try {
      // 1. Get all projects
      const projectsSnapshot = await getDocs(collection(db, 'projects'));
      const projects = projectsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // 2. Get all users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      for (const project of projects) {
        const assignedUsers: any[] = (project as any).assignedUsers || [];
        const assignedUserIds = new Set(assignedUsers.map((u: any) => u.userId));

        // Check each user who has this project in their projects[]
        for (const user of users) {
          const userProjects: string[] = (user as any).projects || [];
          const userId = (user as any).uid || user.id;

          if (userProjects.includes(project.id) && !assignedUserIds.has(userId)) {
            // User has project but project doesn't have user — add to project
            assignedUsers.push({
              userId,
              role: (user as any).role || 'field',
              permissions: [],
              assignedAt: Timestamp.now()
            });
            usersAddedToProjects++;
          }
        }

        // Update project if we added users
        if (usersAddedToProjects > 0 || assignedUsers.length !== ((project as any).assignedUsers || []).length) {
          await updateDoc(doc(db, 'projects', project.id), {
            assignedUsers,
            updatedAt: Timestamp.now()
          });
        }

        // Check each assigned user has this project in their projects[]
        for (const assignedUser of assignedUsers) {
          const userDoc = users.find(u => ((u as any).uid || u.id) === assignedUser.userId);
          if (userDoc) {
            const userProjects: string[] = (userDoc as any).projects || [];
            if (!userProjects.includes(project.id)) {
              // Project has user but user doesn't have project — add to user
              await updateDoc(doc(db, 'users', assignedUser.userId), {
                projects: [...userProjects, project.id],
                updatedAt: Timestamp.now()
              });
              projectsAddedToUsers++;
            }
          }
        }
      }

      setSyncResult({ usersAddedToProjects, projectsAddedToUsers });
      const total = usersAddedToProjects + projectsAddedToUsers;
      if (total === 0) {
        toast.success('All project-user relationships are already in sync!');
      } else {
        toast.success(`Synced ${total} relationships (${usersAddedToProjects} users added to projects, ${projectsAddedToUsers} projects added to users)`);
      }
    } catch (error) {
      console.error('Error syncing project users:', error);
      toast.error('Failed to sync project users');
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    checkPermissions();
  }, [currentUser]);

  const checkPermissions = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const canReset = await canResetData(currentUser.uid);
    setHasPermission(canReset);

    if (canReset) {
      const previewData = await previewReset(currentUser.uid);
      setPreview(previewData);
    }

    setLoading(false);
  };

  const handleReset = async () => {
    if (!currentUser || confirmText !== CONFIRM_PHRASE) {
      toast.error('Please type the confirmation phrase exactly');
      return;
    }

    setIsResetting(true);
    setProgress([]);
    setProgressMessages([]);
    setResult(null);

    try {
      const resetResult = await resetAppData(currentUser.uid, {
        deleteUsers,
        onProgress: (progressOrMessage) => {
          if (typeof progressOrMessage === 'string') {
            setProgressMessages((prev) => [...prev, progressOrMessage]);
          } else {
            setProgress((prev) => {
              const existing = prev.findIndex(
                (p) => p.collection === progressOrMessage.collection
              );
              if (existing >= 0) {
                const updated = [...prev];
                updated[existing] = progressOrMessage;
                return updated;
              }
              return [...prev, progressOrMessage];
            });
          }
        },
      });

      setResult(resetResult);

      if (resetResult.success) {
        toast.success(
          `Reset complete! Deleted ${resetResult.documentsDeleted} documents and ${resetResult.usersDeleted} users.`
        );
      } else {
        toast.error('Reset completed with some errors. Check the details below.');
      }
    } catch (error: any) {
      toast.error(`Reset failed: ${error.message}`);
      setResult({
        success: false,
        collectionsCleared: [],
        documentsDeleted: 0,
        usersDeleted: 0,
        errors: [{ collection: 'general', error: error.message }],
        duration: 0,
      });
    } finally {
      setIsResetting(false);
      setConfirmText('');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-red-800 mb-2">Access Denied</h1>
          <p className="text-red-600">
            You do not have permission to access this page. Only provider administrators can
            reset application data.
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-lg p-6 text-white mb-6">
          <div className="flex items-center">
            <AlertTriangle className="h-10 w-10 mr-4" />
            <div>
              <h1 className="text-2xl font-bold">Data Reset Utility</h1>
              <p className="opacity-90">
                Clear all test data and prepare for production
              </p>
            </div>
          </div>
        </div>

        {/* Sync Project Users Utility */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Link2 className="h-6 w-6 text-indigo-600" />
            <div>
              <h2 className="text-lg font-semibold">Sync Project Users</h2>
              <p className="text-sm text-gray-500">
                Reconcile user.projects[] and project.assignedUsers[] — fixes count mismatches and ensures email notifications reach all team members.
              </p>
            </div>
          </div>

          <button
            onClick={handleSyncProjectUsers}
            disabled={isSyncing}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isSyncing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Run Sync
              </>
            )}
          </button>

          {syncResult && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">Sync Complete</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Users added to projects:</span>
                  <span className="ml-2 font-semibold">{syncResult.usersAddedToProjects}</span>
                </div>
                <div>
                  <span className="text-gray-600">Projects added to users:</span>
                  <span className="ml-2 font-semibold">{syncResult.projectsAddedToUsers}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-800">Warning: Destructive Action</h3>
              <p className="text-amber-700 text-sm mt-1">
                This action will permanently delete all data from the application. This cannot
                be undone. Make sure you have backed up any important data before proceeding.
              </p>
            </div>
          </div>
        </div>

        {/* Preview */}
        {preview && !result && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <Database className="h-5 w-5 mr-2 text-gray-600" />
              Data to be Deleted
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {preview.collections.map((col) => (
                <div
                  key={col.name}
                  className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                >
                  <div className="text-sm text-gray-600">{col.name}</div>
                  <div className="text-xl font-bold text-gray-900">
                    {col.count === -1 ? 'Error' : col.count.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between bg-gray-100 rounded-lg p-4">
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-2 text-gray-600" />
                <span className="font-medium">Test Users to Delete</span>
              </div>
              <span className="text-xl font-bold text-gray-900">{preview.users}</span>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold">Total Documents</span>
                <span className="text-2xl font-bold text-red-600">
                  {preview.total.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Options */}
        {!isResetting && !result && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Reset Options</h2>

            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={deleteUsers}
                onChange={(e) => setDeleteUsers(e.target.checked)}
                className="h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
              />
              <span className="ml-2 text-gray-700">
                Delete test user accounts (keeps your admin account)
              </span>
            </label>
          </div>
        )}

        {/* Confirmation */}
        {!isResetting && !result && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Confirm Reset</h2>
            <p className="text-gray-600 mb-4">
              Type <strong className="text-red-600">{CONFIRM_PHRASE}</strong> to confirm:
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type confirmation phrase..."
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
            />

            <button
              onClick={handleReset}
              disabled={confirmText !== CONFIRM_PHRASE}
              className={`mt-4 w-full flex items-center justify-center px-4 py-3 rounded-md text-white font-medium transition-colors ${
                confirmText === CONFIRM_PHRASE
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              <Trash2 className="h-5 w-5 mr-2" />
              Reset All Data
            </button>
          </div>
        )}

        {/* Progress */}
        {isResetting && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <RefreshCw className="h-5 w-5 mr-2 animate-spin text-indigo-600" />
              Resetting Data...
            </h2>

            <div className="space-y-3">
              {progress.map((p) => (
                <div
                  key={p.collection}
                  className="flex items-center justify-between bg-gray-50 rounded-lg p-3"
                >
                  <div className="flex items-center">
                    {p.status === 'completed' && (
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    )}
                    {p.status === 'in_progress' && (
                      <Loader2 className="h-5 w-5 text-blue-500 mr-2 animate-spin" />
                    )}
                    {p.status === 'error' && (
                      <XCircle className="h-5 w-5 text-red-500 mr-2" />
                    )}
                    {p.status === 'pending' && (
                      <div className="h-5 w-5 border-2 border-gray-300 rounded-full mr-2" />
                    )}
                    <span className="font-medium">{p.collection}</span>
                  </div>
                  <span className="text-gray-600">{p.deleted.toLocaleString()} deleted</span>
                </div>
              ))}

              {progressMessages.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  {progressMessages.slice(-5).map((msg, i) => (
                    <div key={i} className="text-sm text-gray-600 py-1">
                      {msg}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div
            className={`rounded-lg shadow p-6 mb-6 ${
              result.success ? 'bg-green-50' : 'bg-red-50'
            }`}
          >
            <div className="flex items-center mb-4">
              {result.success ? (
                <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
              ) : (
                <XCircle className="h-8 w-8 text-red-500 mr-3" />
              )}
              <h2 className="text-lg font-semibold">
                {result.success ? 'Reset Complete' : 'Reset Completed with Errors'}
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-lg p-3 border">
                <div className="text-sm text-gray-600">Documents Deleted</div>
                <div className="text-xl font-bold">{result.documentsDeleted.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border">
                <div className="text-sm text-gray-600">Users Deleted</div>
                <div className="text-xl font-bold">{result.usersDeleted}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border">
                <div className="text-sm text-gray-600">Collections Cleared</div>
                <div className="text-xl font-bold">{result.collectionsCleared.length}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border">
                <div className="text-sm text-gray-600">Duration</div>
                <div className="text-xl font-bold">{(result.duration / 1000).toFixed(1)}s</div>
              </div>
            </div>

            {result.errors.length > 0 && (
              <div className="bg-red-100 rounded-lg p-4">
                <h3 className="font-semibold text-red-800 mb-2">Errors</h3>
                {result.errors.map((err, i) => (
                  <div key={i} className="text-sm text-red-700 py-1">
                    <strong>{err.collection}:</strong> {err.error}
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => navigate('/')}
              className="mt-6 w-full px-4 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium"
            >
              Return to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataResetPage;
